import { DateRange } from '../value-objects/DateRange';
import { CalendarDate } from '../value-objects/CalendarDate';
import {
  countBusinessDays,
  endOfBusinessWindow,
  firstBusinessDayFrom,
  type NonWorkingDates,
} from './BusinessDayPolicy';
import { assessOverlap, type OccupiedSpan } from './OverlapPolicy';

/**
 * Alternative windows to offer after a rejection.
 *
 * The handoff asks for up to four suggestions of the same length in business
 * days, respecting the sub area's cap and skipping holidays. This is the part of
 * the flow that turns a rejection from a dead end into a next step: the employee
 * is shown windows that would actually be approved rather than being left to
 * guess.
 *
 * The search walks forward from a start date, proposing a window at each
 * candidate business day and keeping the ones that are clear. It is deliberately
 * greedy and non-overlapping — four suggestions one day apart would all breach
 * the same cap and would be four ways of saying the same thing.
 */

export interface SuggestionRequest {
  /** Business days the replacement window must cover — the original length. */
  readonly businessDays: number;
  /** Earliest day a suggestion may start. Usually the day after the rejection. */
  readonly searchFrom: CalendarDate;
  /** Absences already booked in the sub area, excluding the rejected request. */
  readonly existing: readonly OccupiedSpan[];
  readonly cap: number;
  readonly nonWorking: NonWorkingDates;
  /** How many to return. The design shows at most four. */
  readonly limit?: number;
  /** How far ahead to look before giving up. */
  readonly horizonDays?: number;
}

export interface SuggestedWindow {
  readonly range: DateRange;
  readonly businessDays: number;
  /** Holiday dates inside the window, so the UI can mark them. */
  readonly holidaysInside: readonly string[];
  /** How many colleagues are out at the busiest point. Lower is a better offer. */
  readonly peakPeopleOut: number;
}

const DEFAULT_LIMIT = 4;
const DEFAULT_HORIZON_DAYS = 180;

/**
 * Finds clear windows.
 *
 * Returns fewer than `limit` — possibly none — when the calendar genuinely has no
 * room inside the horizon. That is a real answer and the UI says so, rather than
 * padding the list with windows that would be rejected again.
 */
export const suggestWindows = (request: SuggestionRequest): readonly SuggestedWindow[] => {
  const limit = request.limit ?? DEFAULT_LIMIT;
  const horizon = request.horizonDays ?? DEFAULT_HORIZON_DAYS;

  if (request.businessDays <= 0 || limit <= 0) return [];

  const suggestions: SuggestedWindow[] = [];
  let cursor = firstBusinessDayFrom(request.searchFrom, request.nonWorking);
  let stepsTaken = 0;

  while (suggestions.length < limit && stepsTaken < horizon) {
    const end = endOfBusinessWindow(cursor, request.businessDays, request.nonWorking);
    if (end === null) break;

    const candidate = DateRange.create(cursor, end);
    if (!candidate.ok) break;

    const assessment = assessOverlap(candidate.value, request.existing, request.cap);

    if (!assessment.exceedsCap) {
      suggestions.push({
        range: candidate.value,
        businessDays: request.businessDays,
        holidaysInside: holidaysWithin(candidate.value, request.nonWorking),
        peakPeopleOut: assessment.peakPeopleOut,
      });

      // Jump past the window just proposed so the next suggestion is a genuinely
      // different option rather than the same days shifted by one.
      cursor = firstBusinessDayFrom(end.addDays(1), request.nonWorking);
      stepsTaken += candidate.value.naturalDays;
      continue;
    }

    // Blocked: resume the day after the first conflicting day. Advancing a single
    // day at a time would re-test the same blocked stretch over and over.
    const firstConflict = assessment.conflictingDays[0];
    const conflictDate =
      firstConflict === undefined ? null : CalendarDate.fromISO(firstConflict.date);

    const resumeFrom =
      conflictDate !== null && conflictDate.ok
        ? conflictDate.value.addDays(1)
        : cursor.addDays(1);

    // Guaranteed forward progress: `resumeFrom` could in principle land on or
    // before the cursor, and a loop that does not advance would never terminate.
    const advanced = firstBusinessDayFrom(
      CalendarDate.max(resumeFrom, cursor.addDays(1)),
      request.nonWorking,
    );
    stepsTaken += Math.max(1, cursor.daysUntil(advanced));
    cursor = advanced;
  }

  return suggestions;
};

/** Holiday dates falling inside a window, in order. */
const holidaysWithin = (range: DateRange, nonWorking: NonWorkingDates): readonly string[] => {
  const found: string[] = [];
  for (const day of range.days()) {
    const iso = day.toISO();
    if (!day.isWeekend && nonWorking.has(iso)) found.push(iso);
  }
  return found;
};

/**
 * The shortcuts the wizard's date picker offers: a full week, a long weekend
 * bridged to the nearest holiday, and a single day. Each is computed from the
 * anchor date rather than fixed, so "una semana" from a Wednesday still means
 * five business days.
 */
export interface QuickPick {
  readonly key: 'semana' | 'puente' | 'dia';
  readonly label: string;
  readonly range: DateRange;
  readonly businessDays: number;
}

export const quickPicks = (
  anchor: CalendarDate,
  nonWorking: NonWorkingDates,
): readonly QuickPick[] => {
  const picks: QuickPick[] = [];

  const start = firstBusinessDayFrom(anchor, nonWorking);

  const weekEnd = endOfBusinessWindow(start, 5, nonWorking);
  if (weekEnd !== null) {
    const range = DateRange.create(start, weekEnd);
    if (range.ok) {
      picks.push({
        key: 'semana',
        label: 'Una semana',
        range: range.value,
        businessDays: 5,
      });
    }
  }

  // A "puente" bridges to a nearby holiday: three business days, which from a
  // Thursday or a Friday reaches across the weekend.
  const bridgeEnd = endOfBusinessWindow(start, 3, nonWorking);
  if (bridgeEnd !== null) {
    const range = DateRange.create(start, bridgeEnd);
    if (range.ok) {
      picks.push({
        key: 'puente',
        label: 'Puente',
        range: range.value,
        businessDays: 3,
      });
    }
  }

  const single = DateRange.singleDay(start);
  picks.push({
    key: 'dia',
    label: 'Un día',
    range: single,
    businessDays: countBusinessDays(single, nonWorking),
  });

  return picks;
};
